import { languages } from '../../configs/env';
import { keys } from '../../utils/Object';
import { selectMenu } from './selectors';
import {
  Labels,
  Menu,
  Typo3Label,
  Typo3Menu,
  Typo3PathContent,
  Typo3PathContentResponse,
  Typo3State,
  Typo3StaticContent,
  Typo3StaticContentResponse,
  VehicleCenter
} from './types';

/**
 * converts a list of `Typo3Label`s into an object-mapping of
 * `{[clue]: "label"}`
 * @param xs
 */
export const labelsFromTypo3Labels = (xs: Typo3Label[]): Labels =>
  Object.assign({}, ...xs.map(({ clue, value }) => ({ [clue]: value })));

/**
 * catches the given `promise` if it fails and calls the `catcher`
 * @param promise
 * @param catcher
 */
export async function tryFetch<T>(
  promise: Promise<T>,
  catcher: (e: any) => Promise<T>
): Promise<T> {
  try {
    const result = await promise;
    return result;
  } catch (e) {
    return catcher(e);
  }
}

const validQueryParams = ['id', 'L', 'no_cache'];

export const validateQueryParamsPredicate = (key: string) =>
  validQueryParams.includes(key);

export const getSortedVehicleCenters = (
  locations: VehicleCenter[],
  languageCode: string
): VehicleCenter[] => {
  const values: VehicleCenter[] = [...locations];
  values.sort((l0: VehicleCenter, l1: VehicleCenter) =>
    l0.name.localeCompare(l1.name, languageCode)
  );
  return values;
};

export const extractStaticContent = (
  {
    layer_left,
    layer_middle,
    layer_right,
    labels,
    meta
  }: Typo3StaticContentResponse,
  languageUid: number
): Typo3StaticContent => {
  const locale = languages[languageUid];
  const vehicleCenters = getSortedVehicleCenters(
    (meta && meta.usedVehicleCenters) || [],
    locale ? locale.languageCode : 'de'
  );
  const vehicleCentersMap = new Map(
    vehicleCenters.map(
      (center: VehicleCenter): [string, VehicleCenter] => [
        center.searchValue,
        center
      ]
    )
  );
  return {
    layer: {
      left: layer_left[0] || null,
      middle: layer_middle[0] || null,
      right: layer_right[0] || null
    },
    labels: {
      typo3: labelsFromTypo3Labels((labels && labels.data) || []),
      usedVehicles: (labels && labels.usedVehicles) || {},
    },
    vehicleCenters,
    vehicleCentersMap,
    meta,
    sys_language_uid: (meta && meta.sys_language_uid) || languageUid
  };
};

const emptyMenu: Menu = {
  main: [],
  breadcrumb: [],
  meta: [],
  service: [],
  languages: [],
  footer: [],
  search: [],
  searchdetail: [],
  contactpage: [],
  footerlogos: [],
  watchlist: [],
  cookiepage: [],
  servicedirectory: [],
  routeplanner: [],
  rootpage: [],
  magazineoverview: [],
  newsletter: [],
  locations: [],
  financing: [],
  vehicleClassification: [],
  purchaseThankYou: [],
  anniversary10Years: [],
};

const menuEntries: (keyof Menu)[] = keys(emptyMenu);

/**
 * make sure, menu is available, instead use `emptyMenu`
 * @param {?Typo3Menu} menu
 */
const ensureMenu = (menu?: Typo3Menu): Menu =>
  menuEntries.reduce(
    (acc, key) => ({
      ...acc,
      [key]: (menu && menu[key]) || []
    }),
    emptyMenu
  );

export const extractPathContent = ({
  cookie: [cookie],
  content,
  stage,
  meta,
  menu
}: Typo3PathContentResponse): Typo3PathContent => ({
  cookie,
  content,
  stage,
  meta,
  menu: ensureMenu(menu),
  sys_language_uid: (meta && meta.sys_language_uid) || 0
});

export const getVehicleDetailPath = (
  typo3: Typo3State,
  vehicleId: string
) => {
  const [searchDetailEntry = null] = selectMenu(typo3, 'searchdetail');
  if (!searchDetailEntry) {
    return undefined;
  }

  const slug = getVehicleDetailSlug(typo3, vehicleId);
  if (!slug) {
    return undefined;
  }

  return `${searchDetailEntry.link}${slug}`;
};

export const getVehicleDetailSlug = (typo3: Typo3State, vehicleId: string) => {
  return typo3.vehicleSlugs.slugs[vehicleId];
};
